"use client";
import ViewportHeightWrapper from "@/components/ViewportHeightWrapper";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { dateFormats } from "@/utils/dateFormatter";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useLocale } from "next-intl";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import "react-day-picker/style.css";
import { createPortal } from "react-dom";
import DatePickerContent from "./DatePickerContent";

const DatePicker = forwardRef(
  (
    {
      id,
      label,
      drawerLabel,
      value,
      onSelect,
      placeholder,
      disabled,
      onPortalStateChange,
      isFromDate,
      mobileDepartureClick,
      mobileReturnClick,
      departureDate,
      returnDate,
      isRoundTrip,
      defaultMonth,
      cheapestFlightsMonthly,
      cheapestFlightsMonthlyByMonth,
      getCheapestFlightsMonthlyByMonth,
      isMultiCity = false,
      oneWayCalendarPrices,
      publicHolidays,
      scrollToForm,
      viewportHeightWrapperActive = false,
      hasDatePickerTabs = true,
      usePortalForDropdown = false,
    },
    ref,
  ) => {
    const isMobile = useIsMobile();
    const locale = useLocale();
    const t = useTranslationDetector();
    const { currency } = useCurrency();

    // Optimize edilmiş state yönetimi
    const ANIMATION_STATES = useMemo(
      () => ({
        CLOSED: "closed",
        OPENING: "opening",
        OPEN: "open",
        CLOSING: "closing",
      }),
      [],
    );

    const [uiState, setUiState] = useState({
      animationState: ANIMATION_STATES.CLOSED,
      isFocused: false,
      activeTab: "calendar",
    });
    const [portalDropdownPosition, setPortalDropdownPosition] = useState({
      top: 0,
      left: 0,
    });

    // Derived values
    const isOpen = uiState.animationState !== ANIMATION_STATES.CLOSED;
    const isFocused = uiState.isFocused;
    const activeTab = uiState.activeTab;
    const shouldRenderPortal =
      isMobile &&
      (uiState.animationState === ANIMATION_STATES.OPENING ||
        uiState.animationState === ANIMATION_STATES.OPEN ||
        uiState.animationState === ANIMATION_STATES.CLOSING);
    const isAnimating =
      isMobile && uiState.animationState === ANIMATION_STATES.OPEN;
    const shouldRenderWebDropdown =
      !isMobile &&
      (uiState.animationState === ANIMATION_STATES.OPENING ||
        uiState.animationState === ANIMATION_STATES.OPEN ||
        uiState.animationState === ANIMATION_STATES.CLOSING);
    const isWebDropdownAnimating =
      !isMobile && uiState.animationState === ANIMATION_STATES.OPEN;
    const escapePressed = useKeyPressed("Escape");

    const containerRef = useRef(null);
    const webDropdownPortalRef = useRef(null);
    const inputRef = useRef(null);
    const blurTimeoutRef = useRef(null);

    const handleClickOutside = useCallback(() => {
      // Mobilde portal açıksa click outside'ı devre dışı bırak
      if (isMobile && shouldRenderPortal) {
        return;
      }

      // Web'de dropdown açıksa kapat
      if (!isMobile && isOpen) {
        setUiState((prev) => ({
          ...prev,
          animationState: ANIMATION_STATES.CLOSING,
          isFocused: false,
        }));
      }
    }, [isMobile, isOpen, shouldRenderPortal, ANIMATION_STATES]);

    useEffect(() => {
      if (escapePressed && isOpen) {
        setUiState((prev) => ({
          ...prev,
          animationState: ANIMATION_STATES.CLOSING,
          isFocused: false,
        }));
      }
    }, [escapePressed, isOpen, ANIMATION_STATES]);

    // Her zaman ref ver, kontrol handleClickOutside içinde
    useOnClickOutside(
      usePortalForDropdown
        ? [containerRef, webDropdownPortalRef]
        : containerRef,
      handleClickOutside,
    );

    // Portal dropdown pozisyonu - fixed viewport koordinatları
    const updatePortalPosition = useCallback(() => {
      if (
        usePortalForDropdown &&
        containerRef.current &&
        uiState.animationState !== ANIMATION_STATES.CLOSED
      ) {
        const rect = containerRef.current.getBoundingClientRect();
        const dropdownWidth = 860;
        const margin = 8;
        let left = rect.left + rect.width / 2 - dropdownWidth / 2;
        const top = rect.bottom + margin;
        left = Math.max(
          16,
          Math.min(left, window.innerWidth - dropdownWidth - 16),
        );
        setPortalDropdownPosition({ top, left });
      }
    }, [usePortalForDropdown, uiState.animationState, ANIMATION_STATES.CLOSED]);

    useEffect(() => {
      if (
        usePortalForDropdown &&
        !isMobile &&
        shouldRenderWebDropdown &&
        containerRef.current
      ) {
        updatePortalPosition();
        window.addEventListener("scroll", updatePortalPosition, true);
        window.addEventListener("resize", updatePortalPosition);
        return () => {
          window.removeEventListener("scroll", updatePortalPosition, true);
          window.removeEventListener("resize", updatePortalPosition);
        };
      }
    }, [
      usePortalForDropdown,
      isMobile,
      shouldRenderWebDropdown,
      updatePortalPosition,
    ]);

    // Portal state değişikliklerini parent'a bildir
    useEffect(() => {
      if (onPortalStateChange && isMobile) {
        onPortalStateChange(shouldRenderPortal);
      }
    }, [shouldRenderPortal, isMobile, onPortalStateChange]);

    // Body scroll yönetimini parent'a bırak - artık burada yapma
    // useLockBodyScroll(shouldRenderPortal && isMobile);

    // Tek useEffect ile tüm animasyon kontrolü
    useEffect(() => {
      if (uiState.animationState === ANIMATION_STATES.OPENING) {
        const timer = setTimeout(() => {
          setUiState((prev) => ({
            ...prev,
            animationState: ANIMATION_STATES.OPEN,
          }));
        }, 10);
        return () => clearTimeout(timer);
      } else if (uiState.animationState === ANIMATION_STATES.CLOSING) {
        const timer = setTimeout(
          () => {
            setUiState((prev) => ({
              ...prev,
              animationState: ANIMATION_STATES.CLOSED,
            }));
          },
          isMobile ? 300 : 200,
        );
        return () => clearTimeout(timer);
      }
    }, [uiState.animationState, isMobile, ANIMATION_STATES]);

    const handleDateSelect = useCallback(
      (date) => {
        // Clear any pending blur timeout
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }

        if (!isMobile) {
          setUiState((prev) => ({
            ...prev,
            animationState: ANIMATION_STATES.CLOSING,
            isFocused: false,
          }));
        } else {
          handlePortalClose();
        }
        onSelect?.(date);
      },
      [onSelect, ANIMATION_STATES.CLOSING, isMobile],
    );

    // Portal close handler
    const handlePortalClose = useCallback(() => {
      setUiState((prev) => ({
        ...prev,
        animationState: ANIMATION_STATES.CLOSING,
        isFocused: false,
      }));
    }, [ANIMATION_STATES]);

    const handleFocus = useCallback(() => {
      if (!isMobile && scrollToForm) {
        const inputRect = inputRef.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = inputRect.top + scrollTop - 200;

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      }

      // Clear any pending blur timeout
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      setUiState((prev) => ({
        ...prev,
        animationState: ANIMATION_STATES.OPENING,
        isFocused: true,
      }));
    }, [ANIMATION_STATES, isMobile, scrollToForm]);

    // Imperative handle to allow parent components to open the datepicker
    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setUiState((prev) => ({
            ...prev,
            animationState: ANIMATION_STATES.OPENING,
            isFocused: true,
          }));
        },
        close: () => {
          setUiState((prev) => ({
            ...prev,
            animationState: ANIMATION_STATES.CLOSING,
            isFocused: false,
          }));
        },
      }),
      [ANIMATION_STATES.OPENING, ANIMATION_STATES.CLOSING],
    );

    // Blur handler'ı memoize et
    const handleBlur = useCallback(() => {
      // Mobilde portal açıksa blur handler'ı devre dışı bırak
      if (isMobile && shouldRenderPortal) {
        return;
      }

      // Web'de blur handler'ı devre dışı bırak, sadece click outside ile kontrol et
      if (!isMobile) {
        return;
      }

      // Delay blur to allow onClick events to complete
      blurTimeoutRef.current = setTimeout(() => {
        setUiState((prev) => ({
          ...prev,
          animationState: ANIMATION_STATES.CLOSING,
          isFocused: false,
        }));
      }, 150);
    }, [isMobile, shouldRenderPortal, ANIMATION_STATES.CLOSING]);

    const displayValue = value
      ? dateFormats.dayShortMonthShortDayWithComma(value, locale)
      : "";

    // Optimize edilmiş fiyat map'i - O(1) lookup
    const priceMap = useMemo(() => {
      if (!oneWayCalendarPrices || !Array.isArray(oneWayCalendarPrices)) {
        return new Map();
      }
      return new Map(
        oneWayCalendarPrices
          .map((priceItem) => [
            priceItem.date?.toISOString().split("T")[0],
            priceItem.Price,
          ])
          .filter(([date]) => date),
      );
    }, [oneWayCalendarPrices]);

    // O(1) fiyat lookup
    const getPriceByDate = useCallback(
      (targetDate) => {
        const targetDateString = targetDate.toISOString().split("T")[0];
        return priceMap.get(targetDateString) || null;
      },
      [priceMap],
    );

    const cheapestDaysMap = useMemo(() => {
      if (!oneWayCalendarPrices || !Array.isArray(oneWayCalendarPrices)) {
        return new Map();
      }

      return new Map(
        oneWayCalendarPrices
          .map((priceItem) => [
            priceItem.date?.toISOString().split("T")[0],
            priceItem.cheapest === true,
          ])
          .filter(([date]) => date),
      );
    }, [oneWayCalendarPrices]);

    // Kullanım
    const isOneOfCheapestDay = useCallback(
      (date) => {
        const dateStr = date.toISOString().split("T")[0];
        return cheapestDaysMap.get(dateStr) || false;
      },
      [cheapestDaysMap],
    );

    // Tab değişim handler'ı
    const setActiveTab = useCallback((tab) => {
      setUiState((prev) => ({
        ...prev,
        activeTab: tab,
      }));
    }, []);

    // CSS class'ları memoize et
    const containerClasses = useMemo(
      () =>
        clsx(
          "h-[67px] duration-300 ease-in-out md:h-[66px] md:rounded-lg",
          isFocused && "md:ring-primary md:ring-2",
        ),
      [isFocused],
    );

    const labelClasses = useMemo(
      () =>
        clsx(
          "text-foreground-muted pointer-events-none absolute left-[18px] font-medium transition-all duration-300 ease-in-out md:left-5",
          isFocused || displayValue
            ? "top-[14.5px] text-xs"
            : "top-1/2 -translate-y-1/2 text-base",
        ),
      [isFocused, displayValue],
    );

    const inputClasses = useMemo(
      () =>
        clsx(
          "selection:bg-secondary-weak h-full w-full rounded-lg border-0 px-[18px] font-semibold transition-all duration-300 ease-in-out focus:ring-0 focus:outline-none md:px-5",
          isFocused || displayValue
            ? "pt-[32.5px] pb-[12px] md:pt-[31.5px] md:pb-[12.5px]"
            : "py-[12px] md:py-[22.5px]",
        ),
      [isFocused, displayValue],
    );

    // Portal container sınıfları - backdrop yok, direkt tam ekran
    const portalContainerClasses = useMemo(() => {
      return clsx(
        "fixed inset-0 z-50 flex h-full flex-col bg-white",
        "transform transition-all duration-300",
        isAnimating ? "translate-y-0 ease-out" : "translate-y-full ease-in",
      );
    }, [isAnimating]);

    // Web dropdown sınıfları (inline veya portal)
    const webDropdownClasses = useMemo(() => {
      const baseClasses = clsx(
        `${id}-web-dropdown`,
        "shadow-xxl z-50 w-[860px] min-w-[320px] overflow-hidden rounded-lg bg-white",
        "origin-top transform transition-all duration-200 ease-out",
        isWebDropdownAnimating
          ? "translate-y-0 scale-100 opacity-100"
          : "-translate-y-2 scale-95 opacity-0",
      );
      if (usePortalForDropdown) {
        return clsx(baseClasses, "fixed");
      }
      return clsx(
        baseClasses,
        "absolute top-full mt-2",
        isFromDate
          ? "-left-[350px] xl:-left-[200px]"
          : "-left-[450px] xl:-left-[300px]",
      );
    }, [isWebDropdownAnimating, usePortalForDropdown]);

    // Render content component
    const renderDatePickerContent = useCallback(() => {
      return (
        <DatePickerContent
          hasTabs={hasDatePickerTabs}
          mode="single"
          cheapestFlightsMonthly={cheapestFlightsMonthly}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          value={value}
          handleDateSelect={handleDateSelect}
          defaultMonth={defaultMonth}
          getPriceByDate={getPriceByDate}
          isOneOfCheapestDay={isOneOfCheapestDay}
          currency={currency}
          isRoundTrip={isRoundTrip}
          mobileDepartureClick={mobileDepartureClick}
          isFromDate={isFromDate}
          departureDate={departureDate}
          mobileReturnClick={mobileReturnClick}
          returnDate={returnDate}
          isMultiCity={isMultiCity}
          cheapestFlightsMonthlyByMonth={cheapestFlightsMonthlyByMonth}
          getCheapestFlightsMonthlyByMonth={getCheapestFlightsMonthlyByMonth}
          disabled={disabled}
          publicHolidays={publicHolidays}
        />
      );
    }, [
      cheapestFlightsMonthly,
      activeTab,
      setActiveTab,
      value,
      handleDateSelect,
      defaultMonth,
      getPriceByDate,
      isOneOfCheapestDay,
      currency,
      isRoundTrip,
      mobileDepartureClick,
      isFromDate,
      departureDate,
      mobileReturnClick,
      returnDate,
      isMultiCity,
      cheapestFlightsMonthlyByMonth,
      getCheapestFlightsMonthlyByMonth,
      disabled,
      publicHolidays,
    ]);

    return (
      <>
        <div
          className="relative w-full"
          ref={containerRef}
          data-datepicker="true"
        >
          <div className={containerClasses}>
            {label && (
              <label htmlFor={id} className={labelClasses}>
                {label}
              </label>
            )}
            <input
              ref={inputRef}
              type="text"
              id={id}
              readOnly
              value={displayValue}
              placeholder={placeholder}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className={inputClasses}
            />
          </div>
          {/* Web dropdown - inline (parent overflow'a tabi) */}
          {!isMobile && shouldRenderWebDropdown && !usePortalForDropdown && (
            <div className={webDropdownClasses}>
              <ViewportHeightWrapper
                enabled={viewportHeightWrapperActive}
                defaultMaxHeight={525}
                bottomPadding={16}
              >
                {renderDatePickerContent()}
              </ViewportHeightWrapper>
            </div>
          )}
        </div>

        {/* Web dropdown portal - parent overflow'tan bağımsız */}
        {usePortalForDropdown &&
          !isMobile &&
          shouldRenderWebDropdown &&
          createPortal(
            <div
              ref={webDropdownPortalRef}
              className={webDropdownClasses}
              style={{
                top: portalDropdownPosition.top,
                left: portalDropdownPosition.left,
              }}
            >
              <ViewportHeightWrapper
                enabled={viewportHeightWrapperActive}
                defaultMaxHeight={525}
                bottomPadding={16}
              >
                {renderDatePickerContent()}
              </ViewportHeightWrapper>
            </div>,
            document.body,
          )}

        {/* Mobile portal - tam ekran slide up modal */}
        {shouldRenderPortal &&
          isMobile &&
          createPortal(
            <div className={portalContainerClasses}>
              {/* Header */}
              <div className="flex shrink-0 items-center justify-between px-4 py-4">
                <span className="w-full pl-[30px] text-center text-lg font-semibold">
                  {drawerLabel || label}
                </span>
                <button onClick={handlePortalClose} className="self-end">
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["xmark"]}
                    className="text-2xl"
                  />
                </button>
              </div>

              {/* Content */}
              <div className="min-h-0 flex-1">
                {renderDatePickerContent()}
                {/* <div
                  className={clsx(
                    "border-primary-weak relative z-[1] shrink-0 border-t bg-white p-4 md:hidden",
                    activeTab !== "calendar" && "hidden",
                  )}
                >
                  <Button
                    onClick={handlePortalClose}
                    className="w-full"
                    size="lg"
                  >
                    {t("common.apply")}
                  </Button>
                </div> */}
              </div>
            </div>,
            document.body,
          )}
      </>
    );
  },
);

DatePicker.displayName = "DatePicker";

export default DatePicker;
